import * as CustomerDetailSelectors from 'src/admin-app/customers/customer-detail/+state/customer-detail.selectors';
import * as QuoteDetailActions from './quote-detail.actions';
import * as QuoteDetailSelectors from './quote-detail.selectors';
import * as RailroadDetailSelectors from 'src/admin-app/railroads/railroad-detail/+state/railroad-detail.selectors';

import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ActivatedRouteSnapshot, Router } from '@angular/router';
import { ContractRates, JobsClient, QuoteLogAction } from 'src/shared/api';
import { QUOTE_STATUSES, getAllListArgs } from 'src/shared/constants';
import {
	QuoteDetailPartialState,
	quoteDetailFeatureKey,
} from './quote-detail.reducer';
import { Store, select } from '@ngrx/store';
import {
	buildQuoteLog,
	hasRates,
	quoteFormToQuote,
} from '../quote-detail.utility';
import {
	concatMap,
	debounce,
	exhaustMap,
	filter,
	first,
	map,
	mapTo,
	switchMap,
	tap,
	withLatestFrom,
} from 'rxjs/operators';

import { AlertDialogComponent } from 'src/shared/alerts/alert-dialog/alert-dialog.component';
import { AuthService } from 'src/admin-app/shared/auth/auth.service';
import { DataPersistence } from '@nrwl/angular';
import { FormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { LookUpsService } from 'src/shared/lookups.service';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { QuoteDetailComponent } from '../quote-detail.component';
import { QuoteReportDialogComponent } from '../quote-report-dialog/quote-report-dialog.component';
import { RailProsError } from 'src/shared/models/rail-pros-error.model';
import { ReportsService } from 'src/admin-app/shared/services/reports.service';
import { Roles } from 'src/shared/enums';
import { grabIdFromParams } from 'src/shared/utilities/utility-functions';
import { isEmpty } from 'lodash';
import { jobsFragments } from 'src/admin-app/jobs/jobs.routes';
import { notifyAfterSaving } from 'src/shared/utilities/custom-rxjs-operators';
import { of } from 'rxjs';
import { quotesFragments } from '../../quotes.routes';

@Injectable()
export class QuoteDetailEffects {
	selectQuote$ = createEffect(() =>
		this.dataPersistence.navigation(QuoteDetailComponent, {
			run: (
				r: ActivatedRouteSnapshot,
				_state: QuoteDetailPartialState
			) => {
				const quoteId = grabIdFromParams(r.paramMap);

				return QuoteDetailActions.quoteSelected({
					quoteId,
				});
			},
			onError: (_a: ActivatedRouteSnapshot, error: any) => {
				throw new Error(error);
			},
		})
	);

	navigateToJob$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteDetailActions.quoteTurnedIntoJob),
				withLatestFrom(
					this.store.pipe(
						select(QuoteDetailSelectors.getSelectedQuoteId)
					),
					(_id, quoteId) => quoteId
				),
				filter((id) => id > 0),
				tap((jobId) => {
					this.router.navigate(jobsFragments.detail(jobId));
				})
			),
		{ dispatch: false }
	);

	loadQuoteFromStore$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.quoteSelected),
			map((action) => action.quoteId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(select(QuoteDetailSelectors.getSelectedQuote)),
				(_id, quote) => quote
			),
			filter(Boolean),
			map((quote) => QuoteDetailActions.quoteLoadedFromStore({ quote }))
		)
	);

	loadQuoteFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.quoteSelected),
			map((action) => action.quoteId),
			filter((id) => id > 0),
			withLatestFrom(
				this.store.pipe(select(QuoteDetailSelectors.getSelectedQuote))
			),
			filter(([_id, quote]) => !quote),
			concatMap(([id, _quote]) =>
				this.jobsClient.getJob(id).pipe(
					map((quote) =>
						QuoteDetailActions.quoteLoadedFromApi({
							quote,
						})
					)
				)
			)
		)
	);

	guardAgainstApprovedJob$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					QuoteDetailActions.quoteLoadedFromApi,
					QuoteDetailActions.quoteLoadedFromStore
				),
				filter(
					({ quote }) =>
						!QUOTE_STATUSES.includes(quote.status) &&
						!(
							this.authService.userRoles.includes(Roles.Admin) ||
							this.authService.userRoles.includes(
								Roles.FieldDirector
							)
						)
				),
				tap(({ quote }) =>
					this.router.navigate(jobsFragments.detail(quote.id))
				)
			),
		{ dispatch: false }
	);

	requestRailroad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				QuoteDetailActions.quoteLoadedFromApi,
				QuoteDetailActions.quoteLoadedFromStore
			),
			map((action) => action.quote),
			filter((quote) => !!quote.railroad),
			map((quote) => quote.railroad.id),
			map((railroadId) =>
				QuoteDetailActions.railroadRequested({ railroadId })
			)
		)
	);

	requestCustomer$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				QuoteDetailActions.quoteLoadedFromApi,
				QuoteDetailActions.quoteLoadedFromStore
			),
			map((action) => action.quote),
			filter((quote) => !!quote.customer),
			map((quote) => quote.customer.id),
			map((customerId) =>
				QuoteDetailActions.customerRequested({ customerId })
			)
		)
	);

	startNewQuote$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.quoteSelected),
			map((action) => action.quoteId),
			filter((id) => id === 0),
			mapTo(QuoteDetailActions.newQuoteStarted())
		)
	);

	saveQuote$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			QuoteDetailActions.saveRequested,
			{
				run: (
					action: ReturnType<typeof QuoteDetailActions.saveRequested>,
					_state: QuoteDetailPartialState
				) => {
					const q = quoteFormToQuote(action.formValue);

					return this.jobsClient.saveJob(q).pipe(
						switchMap((id) =>
							q.id === 0 ? this.jobsClient.getJob(id) : of(q)
						),
						map((quote) =>
							q.id === 0
								? QuoteDetailActions.newQuoteSaved({
									quote,
								})
								: QuoteDetailActions.quoteSaved({
									quote,
								})
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof QuoteDetailActions.saveRequested
					>,
					error: any
				) => {
					return QuoteDetailActions.errorSavingQuote({
						error,
					});
				},
			}
		)
	);

	confirmPrevailingWage$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			QuoteDetailActions.userConfirmedPrevailingWage,
			{
				run: (
					action: ReturnType<
						typeof QuoteDetailActions.userConfirmedPrevailingWage
					>,
					_state: QuoteDetailPartialState
				) => {
					const q = quoteFormToQuote(action.quoteForm);

					if (
						!q.prevailingWageInfo ||
						isEmpty(q.prevailingWageInfo)
					) {
						throw new RailProsError(
							450,
							'',
							'Prevailing Wage Information must be set before it can be confirmed.'
						);
					}
					if (
						q.prevailingWageInfo.confirmedBy ||
						q.prevailingWageInfo.confirmedDate
					) {
						throw new RailProsError(
							450,
							'',
							'Prevailing Wage has already been confirmed.'
						);
					}
					return this.jobsClient.saveJob(q).pipe(
						exhaustMap((id) =>
							this.jobsClient.confirmPrevailingWages(id)
						),
						map((response) =>
							QuoteDetailActions.prevailingWagesConfirmed({
								prevailingWageInfo: {
									...q.prevailingWageInfo,
									...response,
								},
							})
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof QuoteDetailActions.userConfirmedPrevailingWage
					>,
					error: any
				) => {
					return QuoteDetailActions.errorSavingQuote({
						error,
					});
				},
			}
		)
	);

	throwIfError$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					QuoteDetailActions.errorSavingQuote,
					QuoteDetailActions.errorLoadingQuote
				),
				map((action) => action.error),
				map((error) => {
					throw Error(error);
				})
			),
		{ dispatch: false }
	);

	closeAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					QuoteDetailActions.quoteSaved,
					QuoteDetailActions.newQuoteSaved
				),
				withLatestFrom(
					this.store.pipe(
						select(QuoteDetailSelectors.getCloseAfterSaving)
					),
					(_, shouldClose) => shouldClose
				),
				filter(Boolean),
				tap(() => this.router.navigate(quotesFragments.list))
			),
		{ dispatch: false }
	);

	navigateToCreatedQuote$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteDetailActions.newQuoteSaved),
				map((action) => action.quote.id),
				withLatestFrom(
					this.store.pipe(
						select(QuoteDetailSelectors.getCloseAfterSaving)
					)
				),
				filter(([_, shouldClose]) => !shouldClose),
				tap(([id, _]) =>
					this.router.navigate(quotesFragments.detail(id))
				)
			),
		{ dispatch: false }
	);

	syncRatesToOfferedServices$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.offeredServicesUpdated),
			debounce(() =>
				this.actions$.pipe(
					ofType(QuoteDetailActions.offeredServicesToggled)
				)
			),
			map(({ offeredServices }) => offeredServices.map((o) => o.type.id)),
			withLatestFrom(
				this.store.pipe(select(QuoteDetailSelectors.getContractRates)),
				this.store.pipe(select(QuoteDetailSelectors.getSelectedQuote)),
				this.lookupService.serviceTypes,
				(newServiceTypeIds, updatedRates, quote, serviceTypes) => {
					const oldServiceTypeIds = (
						updatedRates ||
						quote.contractedRates ||
						[]
					).map((rate) => rate.serviceType.id);
					const serviceIdsToAdd = newServiceTypeIds.filter(
						(n) => !oldServiceTypeIds.includes(n)
					);
					const servicesToRemove = oldServiceTypeIds.filter(
						(o) => !newServiceTypeIds.includes(o)
					);

					const servicesToAdd = serviceIdsToAdd.map((id) =>
						serviceTypes.find((t) => t.id === id)
					);
					return { servicesToAdd, servicesToRemove };
				}
			),
			exhaustMap(({ servicesToAdd, servicesToRemove }) => {
				const actions = [];
				if (servicesToAdd.length > 0) {
					actions.push(
						QuoteDetailActions.servicesAdded({
							serviceTypes: servicesToAdd,
						})
					);
				}
				if (servicesToRemove.length > 0) {
					actions.push(
						QuoteDetailActions.servicesRemoved({
							serviceTypeIds: servicesToRemove,
						})
					);
				}

				return actions;
			})
		)
	);

	applyRailroadRates$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.railroadSelected),
			map((action) => action.railroadId),
			switchMap((id) =>
				this.store.pipe(
					select(RailroadDetailSelectors.getRailroadEntity(id)),
					filter(Boolean),
					first()
				)
			),
			filter(hasRates),
			exhaustMap((railroad) =>
				this.dialog
					.open(AlertDialogComponent, {
						data: {
							message:
								"Apply Railroad's contracted rates to this job?",
						},
					})
					.afterClosed()
					.pipe(
						filter(Boolean),
						mapTo(railroad.contractedRates as ContractRates[])
					)
			),
			map((contractedRates) =>
				QuoteDetailActions.railroadRatesApplied({ contractedRates })
			)
		)
	);

	loadNewRailroad$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.railroadSelected),
			map((action) => action.railroadId),
			switchMap((id) =>
				this.store.pipe(
					select(RailroadDetailSelectors.getRailroadEntity(id)),
					filter(Boolean),
					first()
				)
			),
			map((railroad) =>
				QuoteDetailActions.updatedRailroadLoaded({ railroad })
			)
		)
	);

	applyCustomerRates$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.customerSelected),
			map((action) => action.customerId),
			exhaustMap((id) =>
				this.store.pipe(
					select(CustomerDetailSelectors.getCustomerEntity(id)),
					filter(Boolean),
					first()
				)
			),
			filter(hasRates),
			exhaustMap((customer) =>
				this.dialog
					.open(AlertDialogComponent, {
						data: {
							message:
								"Apply Customer's contracted rates to this job?",
						},
					})
					.afterClosed()
					.pipe(
						filter(Boolean),
						mapTo(customer.contractedRates as ContractRates)
					)
			),
			map((contractedRates: ContractRates) =>
				QuoteDetailActions.customerRatesApplied({ contractedRates })
			)
		)
	);

	confirmPrint$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.printQuoteReportClicked),
			exhaustMap(() =>
				this.dialog
					.open(QuoteReportDialogComponent, {
						minWidth: '30rem',
					})
					.afterClosed()
					.pipe(
						filter<FormGroup>(Boolean),
						map((form) =>
							form.value ? form.value.addToDocuments : false
						),
						map((addToDocuments) =>
							QuoteDetailActions.printQuoteReportConfirmed({
								addToDocuments,
							})
						)
					)
			)
		)
	);

	printQuoteReport$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.printQuoteReportConfirmed),
			map((action) => action.addToDocuments),
			withLatestFrom(
				this.store.pipe(select(QuoteDetailSelectors.getSelectedQuoteId))
			),
			exhaustMap(([addToDocuments, quoteId]) =>
				this.reportService
					.downloadQuoteReport(quoteId, addToDocuments)
					.pipe(
						map(() =>
							QuoteDetailActions.quoteReportPrinted({
								addToDocuments,
							})
						)
					)
			)
		)
	);

	loadDocuments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.quoteReportPrinted),
			map((action) => action.addToDocuments),
			filter(Boolean),
			withLatestFrom(
				this.store.pipe(
					select(QuoteDetailSelectors.getSelectedQuoteId)
				),
				(_, quoteId) => quoteId
			),
			switchMap((quoteId) => this.jobsClient.getDocumentsForJob(quoteId)),
			map((documents) =>
				QuoteDetailActions.documentsLoaded({ documents })
			)
		)
	);

	notifyAfterSaving$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(
					QuoteDetailActions.quoteSaved,
					QuoteDetailActions.newQuoteSaved
				),
				notifyAfterSaving(this.snackbar)
			),
		{ dispatch: false }
	);

	loadQuoteLogsFromApi$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.quoteSelected),
			map((action) => action.quoteId),
			filter<number>(Boolean),
			switchMap((jobId) =>
				this.jobsClient.getQuoteLogs({ ...getAllListArgs, jobId })
			),
			map((pagedQuoteLogResults) =>
				QuoteDetailActions.pagedQuoteLogsLoaded({
					pagedQuoteLogResults,
				})
			)
		)
	);

	refreshQuoteLogs$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				QuoteDetailActions.quoteLogAdded,
				QuoteDetailActions.quoteSaved,
				QuoteDetailActions.quoteApprovedByRailPros
			),
			withLatestFrom(
				this.store.pipe(
					select(QuoteDetailSelectors.getSelectedQuoteId)
				),
				(_, id) => id
			),
			switchMap((jobId) =>
				this.jobsClient.getQuoteLogs({ ...getAllListArgs, jobId })
			),
			map((pagedQuoteLogResults) =>
				QuoteDetailActions.pagedQuoteLogsLoaded({
					pagedQuoteLogResults,
				})
			)
		)
	);

	displayMessage$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(QuoteDetailActions.errorApprovingQuote),
				tap((action) => {
					if (action.errorApprovingQuote.status === 450) {
						throw new RailProsError(
							450,
							'',
							action.errorApprovingQuote.response
						);
					}
					this.dialog.open(AlertDialogComponent, {
						data: {
							message:
								'Cannot approve Quote. Some fields are invalid.',
						},
					});
				})
			),
		{ dispatch: false }
	);

	sendNote$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			QuoteDetailActions.noteSubmitted,
			{
				run: (
					action: ReturnType<typeof QuoteDetailActions.noteSubmitted>,
					state: QuoteDetailPartialState
				) => {
					const quoteId =
						state[quoteDetailFeatureKey].selectedQuoteId;
					const comment = action.note;
					const log = buildQuoteLog(
						quoteId,
						QuoteLogAction.CommentToCustomer,
						comment
					);
					return this.jobsClient.addQuoteLog(log).pipe(
						map((id) => ({ ...log, id })),
						map((quoteLog) =>
							QuoteDetailActions.quoteLogAdded({ quoteLog })
						)
					);
				},
				onError: (
					_action: ReturnType<
						typeof QuoteDetailActions.noteSubmitted
					>,
					error: any
				) => {
					return QuoteDetailActions.errorAddingQuoteLog({
						error,
					});
				},
			}
		)
	);

	confirmApprove$ = createEffect(() =>
		this.actions$.pipe(
			ofType(QuoteDetailActions.approveQuoteClicked),
			exhaustMap((action) =>
				this.dialog
					.open(AlertDialogComponent, {
						data: {
							message: 'Approve quote?',
						},
					})
					.afterClosed()
					.pipe(
						filter(Boolean),
						map(() =>
							QuoteDetailActions.approveQuoteConfirmed({
								note: action.note,
								formValue: action.formValue,
							})
						)
					)
			)
		)
	);

	approveQuote$ = createEffect(() =>
		this.dataPersistence.pessimisticUpdate(
			QuoteDetailActions.approveQuoteConfirmed,
			{
				run: (
					action: ReturnType<
						typeof QuoteDetailActions.approveQuoteConfirmed
					>,
					state: QuoteDetailPartialState
				) => {
					const quoteId =
						state[quoteDetailFeatureKey].selectedQuoteId;
					const quote = quoteFormToQuote(action.formValue);
					const log = buildQuoteLog(
						quoteId,
						QuoteLogAction.RailProsApproval,
						'Quote Approved'
					);
					return this.jobsClient.saveJob(quote).pipe(
						exhaustMap(() => this.jobsClient.addQuoteLog(log)),
						map(() => QuoteDetailActions.quoteTurnedIntoJob())
					);
				},
				onError: (
					_action: ReturnType<
						typeof QuoteDetailActions.approveQuoteConfirmed
					>,
					errorApprovingQuote: any
				) => {
					return QuoteDetailActions.errorApprovingQuote({
						errorApprovingQuote,
					});
				},
			}
		)
	);

	notifyOfInvalidContractedRates$ = createEffect(() => 
		this.actions$.pipe(
			ofType(QuoteDetailActions.invalidContractedRates),
			exhaustMap(() => 
				this.dialog.open(AlertDialogComponent, {
					data: {
						showFailureButton: false,
						message: 'Invalid or misconfigured Contracted Rates',
					},
				})
				.afterClosed()
			)
		),
		{ dispatch: false, }
	);

	constructor(
		private readonly actions$: Actions,
		private readonly store: Store<QuoteDetailPartialState>,
		private readonly reportService: ReportsService,
		private readonly dataPersistence: DataPersistence<
			QuoteDetailPartialState
		>,
		private readonly dialog: MatDialog,
		private readonly jobsClient: JobsClient,
		private readonly router: Router,
		private readonly snackbar: MatSnackBar,
		private readonly lookupService: LookUpsService,
		private readonly authService: AuthService
	) { }
}
